import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { createApiClients, type ApiClients, type ApiClientConfig } from '@/api/client';

type ApiContextValue = {
  clients: ApiClients;
  config: ApiClientConfig;
};

const ApiContext = createContext<ApiContextValue | null>(null);

export type ApiProviderProps = ApiClientConfig & {
  children: ReactNode;
};

/**
 * Threads the typed-client bundle through the React tree. Screens call
 * `useApiClients()` and pick the per-service client they need
 * (`clients.asset`, `clients.alert`, etc.). `useApiConfig()` exposes
 * the same baseUrl + token supplier so screens that still call
 * Phase-3-deferred endpoints via apiFetch get the same auth header
 * the typed clients would inject.
 */
export function ApiProvider({ baseUrl, getAuthToken, children }: ApiProviderProps) {
  const value = useMemo<ApiContextValue>(() => {
    const config: ApiClientConfig = { baseUrl, getAuthToken };
    return { clients: createApiClients(config), config };
  }, [baseUrl, getAuthToken]);
  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
}

export function useApiClients(): ApiClients {
  const ctx = useContext(ApiContext);
  if (!ctx) {
    throw new Error('useApiClients must be used inside <ApiProvider>');
  }
  return ctx.clients;
}

export function useApiConfig(): ApiClientConfig {
  const ctx = useContext(ApiContext);
  if (!ctx) {
    throw new Error('useApiConfig must be used inside <ApiProvider>');
  }
  return ctx.config;
}
